import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { InputActionMeta, PropsValue, SingleValue } from 'react-select';
import debounce from 'lodash/debounce';

import { UserRoles } from 'shared/enums';
import {
  removeTokenFromLocalStorage,
  getTokenFromLocalStorage
} from 'shared/utils/local-storage';
import { http } from 'shared/utils/axios';
import { Text } from 'shared/components/Text';
import {
  LogoHeader,
  SettingsIcon,
  PersonIcon,
  LogOutIcon,
  ChatIcon
} from 'shared/components/Icons';
import Search from 'shared/components/Search';
import { BasicPopOver } from 'shared/components/PopOver';
import { createSearchOptions, TSearchData } from './searchOptions';
import { getCurrentUserSelector, getUser } from 'store/settings/settings.redux';
import {
  getAffiliateSelector,
  getCurrentAffiliate
} from 'store/settings/settings.redux';

import classes from './styles.module.scss';
import { TOption } from 'shared/types';
import { getUnreadCount, getUnreadCountSelector } from 'store/chat/chat.redux';
import { Badge } from 'antd';

type TLoadedOptions = {
  label: string;
  options: TOption[];
};

export const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentAffiliate = useSelector(getAffiliateSelector);
  const currentUser = useSelector(getCurrentUserSelector);
  const unreadCount = useSelector(getUnreadCountSelector);
  const [inputValue, setInputValue] = useState<string>('');
  const [value, setValue] = useState<
    PropsValue<{ value: string; label: string }> | undefined
  >(null);

  useEffect(() => {
    dispatch(getCurrentAffiliate());
    dispatch(getUser());
  }, [dispatch]);

  useEffect(() => {
    if (!currentAffiliate?.id) {
      return;
    }

    dispatch(getUnreadCount({ affiliateId: currentAffiliate.id }));
  }, [currentAffiliate?.id, dispatch]);

  const isAffiliateSuperAgent = useMemo(
    () => currentUser?.role === UserRoles.AffiliateAgent,
    [currentUser]
  );

  const onFocus = () => setValue(null);

  const handleChange = (option: SingleValue<TOption>) => {
    switch (option?.type) {
      case 'order':
        return navigate(`/orders/${option.value}`);
      case 'patient':
        return navigate(`/patients/${option.value}`);

      default:
        break;
    }
  };

  const loadOptions = async (
    inputValue: string,
    callback: (options: TLoadedOptions[]) => void
  ) => {
    if (inputValue.length >= 3) {
      const token = getTokenFromLocalStorage();
      const { data: searchData } = await http<TSearchData>({
        url: `/search?access_token=${token}&q=${inputValue}`,
        method: 'GET'
      });
      const loadedOptions = createSearchOptions(searchData);
      callback(loadedOptions);
    }
  };

  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 500), []);

  const getOptions = (
    inputValue: string,
    callback: (options: TLoadedOptions[]) => void
  ) => {
    debouncedLoadOptions(inputValue, callback);
  };

  const content = [
    {
      iconRight: <PersonIcon />,
      label: 'Profile',
      key: 'profile',
      onClick: () => {
        navigate('/');
      }
    },
    {
      iconRight: <LogOutIcon />,
      label: 'Log Out',
      key: 'logout',
      onClick: () => {
        removeTokenFromLocalStorage();
        navigate('/login');
      }
    }
  ];
  const onInputChange = (inputValue: string, { action }: InputActionMeta) => {
    if (action === 'input-change') {
      setInputValue(inputValue);
    }
  };
  return (
    <div className={classes.header}>
      <LogoHeader />
      <section className={classes.header_search}>
        <Search
          asyncSelect
          value={value}
          loadOptions={getOptions}
          width="456px"
          onChange={handleChange}
          onFocus={onFocus}
          onInputChange={onInputChange}
          inputValue={inputValue}
          setInputValue={setInputValue}
          className={classes.customInput}
        />
      </section>
      <div className={classes['header__icons']}>
        {currentAffiliate?.affiliateChatSystem && (
          <div className={classes['header__icons__chat-icon']}>
            <Link to={'/messages'}>
              <Badge
                count={unreadCount}
                size={'small'}
                overflowCount={9}
                offset={[5, -1]}
              >
                <ChatIcon color="#30313D" />
              </Badge>
            </Link>
          </div>
        )}

        <Text
          type="sf-caption"
          color="grey-eighth"
          className={classes.header_text}
        >
          {currentAffiliate?.name}
        </Text>
        <Link
          to={
            isAffiliateSuperAgent
              ? '/settings/user-management'
              : '/settings/details'
          }
        >
          <SettingsIcon />
        </Link>
        <BasicPopOver content={content} trigger={'click'} placement="bottom">
          <div className={classes['header__icons__person-icon']}>
            <PersonIcon />
          </div>
        </BasicPopOver>
      </div>
    </div>
  );
};
export default Header;
